//------------------------------------------------------------------------
// Protocol Navigation (adapted for Rally)
// https://protocol.mozilla.org/components/detail/navigation--default.html
// https://github.com/mozilla/protocol/blob/main/assets/js/protocol/protocol-navigation.js
//------------------------------------------------------------------------
import Supports from './protocol-supports';

export default class Navigation {
  constructor(el, options) {
    this._navElem = el;
    this._navItemsLists;
    this._ticking = false;
    this._lastKnownScrollPosition = 0;
    this._animationFrameID = null;
    this._stickyScrollOffset = 300;
    this._wideBreakpoint = getComputedStyle(document.documentElement).getPropertyValue('--layout-nav-bp') || '1024px';
    this._tallBreakpoint = '600px';
    this._mqLargeNav;
    this._viewport = document.getElementsByTagName('html')[0];

    // Use Object.assign() to merge options with defaults
    this._options = Object.assign(
      {},
      {
        onNavOpen: null,
        onNavClose: null,
      },
      options
    );

    this.init();
  }

  /**
   * Does the viewport meet the minimum width and height
   * requirements for sticky behaviour?
   * @returns {Boolean}
   */
  isLargeViewport() {
    return this._mqLargeNav.matches;
  }

  /**
   * Feature detect for sticky navigation
   * @returns {Boolean}
   */
  supportsSticky() {
    if (typeof Supports !== 'undefined') {
      return (
        Supports.matchMedia &&
        Supports.classList &&
        Supports.requestAnimationFrame &&
        Supports.cssFeatureQueries &&
        CSS.supports('position', 'sticky')
      );
    } else {
      return false;
    }
  }

  /**
   * Scroll event listener. No computationally expensive
   * operations such as DOM modifications should happen
   * here. Instead we throttle using `requestAnimationFrame`.
   */
  onScroll() {
    if (!this._ticking) {
      this._animationFrameID = window.requestAnimationFrame(this.checkScrollPosition.bind(this));
      this._ticking = true;
    }
  }

  /**
   * Create sticky state for the navigation.
   */
  createSticky() {
    this._viewport.classList.add('mzp-has-sticky-navigation');
    this._animationFrameID = window.requestAnimationFrame(this.checkScrollPosition.bind(this));
    window.addEventListener('scroll', this.onScroll.bind(this), false);
  }

  /**
   * Destroy sticky state for the navigation.
   */
  destroySticky() {
    this._viewport.classList.remove('mzp-has-sticky-navigation');
    this._navElem.classList.remove('mzp-is-scrolling');
    this._navElem.classList.remove('mzp-is-hidden');
    this._lastKnownScrollPosition = 0;

    if (this._animationFrameID) {
      window.cancelAnimationFrame(this._animationFrameID);
    }
    window.removeEventListener('scroll', this.onScroll, false);
  }

  /**
   * Initialize sticky state for the navigation.
   * Uses `matchMedia` to determine if conditions
   * for sticky navigation are satisfied.
   */
  initSticky() {
    this._mqLargeNav = matchMedia(
      '(min-width: ' +
        this._wideBreakpoint +
        ') and (min-height: ' +
        this._tallBreakpoint +
        ')'
    );

    this._mqLargeNav.addListener((mq) => {
      if (mq.matches) {
        this.createSticky();
      } else {
        this.destroySticky();
      }
    });

    if (this.isLargeViewport()) {
      this.createSticky();
    }
  }

  /**
   * Implements sticky navigation behaviour as
   * user scrolls up and down the viewport.
   */
  checkScrollPosition() {
    // add styling for when scrolling the viewport
    if (window.scrollY > 0) {
      this._navElem.classList.add('mzp-is-scrolling');
    } else {
      this._navElem.classList.remove('mzp-is-scrolling');
    }

    // scrolling down
    if (window.scrollY > this._lastKnownScrollPosition) {
      // hide the sticky nav shortly after scrolling down the viewport.
      if (window.scrollY > this._stickyScrollOffset) {
        // if there's a menu currently open, close it.
        // if (typeof Mzp.Menu !== 'undefined') {
        //   Mzp.Menu.close();
        // }

        this._navElem.classList.add('mzp-is-hidden');

        // Threespot: Close any open expandable menus
        this._navElem.querySelectorAll('[aria-expanded="true"]').forEach((el) => {
          el.click();
        });
      }
    }
    // scrolling up
    else {
      this._navElem.classList.remove('mzp-is-hidden');
    }

    this._lastKnownScrollPosition = window.scrollY;
    this._ticking = false;
  }

  /**
   * Event handler for navigation menu button `click` events.
   */
  onClick(e) {
    var thisNavItemList = e.target.parentNode.querySelector(
      '.mzp-c-navigation-items'
    );

    e.preventDefault();

    // Update button state
    e.target.classList.toggle('mzp-is-active');

    // Update menu state
    thisNavItemList.classList.toggle('mzp-is-open');

    // Update aria-expended state on menu.
    var expanded = thisNavItemList.classList.contains('mzp-is-open')
      ? true
      : false;
    thisNavItemList.setAttribute('aria-expanded', expanded);

    if (expanded) {
      if (typeof this._options.onNavOpen === 'function') {
        this._options.onNavOpen(thisNavItemList);
      }
    } else {
      if (typeof this._options.onNavClose === 'function') {
        this._options.onNavClose(thisNavItemList);
      }
    }
  }

  /**
   * Set initial ARIA navigation states.
   */
  setAria() {
    for (var i = 0; i < this._navItemsLists.length; i++) {
      this._navItemsLists[i].setAttribute('aria-expanded', false);
    }
  }

  /**
   * Bind navigation event handlers.
   */
  bindEvents() {
    this._navItemsLists = document.querySelectorAll('.mzp-c-navigation-items');
    if (this._navItemsLists.length > 0) {
      var navButtons = document.querySelectorAll(
        '.mzp-c-navigation-menu-button'
      );
      for (var i = 0; i < navButtons.length; i++) {
        navButtons[i].addEventListener('click', this.onClick, false);
      }
      this.setAria();
    }
  }

  /**
   * Initialise menu.
   * @param {Object} options - configurable options.
   */
  init(options) {
    this.bindEvents();

    /**
     * Init (optional) sticky navigation.
     * If there are multiple navigation organisms on a single page,
     * assume only the first (and hence top-most) instance can and
     * will be sticky.
     */
    if (this._navElem && this._navElem.classList.contains('mzp-is-sticky')) {
      if (this.supportsSticky()) {
        this.initSticky();
      }
    }
  }
}
